import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Formik, FormikProps, Form, FormikHelpers } from "formik";
import { Grid, Button, Typography, styled, alpha } from "@mui/material";
// Redux Imports
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IStructuresState, IAirportOption } from "src/redux/reducers/StructuresReducer";
import { formatIdentFromAirport, updateAirportOptions } from "src/utils/airportHelper";
import { FormikSelectField, FormikDateTimeField, FormikTextField } from "../../../FormikComponents/FormikWrappers";
import { SwitchField } from "../../../FormikComponents/SwitchField";
import {
  ICrewsNonFlyingAssignment,
  INonFlyingAssignment,
  INonFlyingType,
  IPairing,
  IUser,
} from "@vmosolutions/oc-interfaces";
import * as Yup from "yup";
import { formatUserLabel } from "src/components/Admin/Tabs/Users/ManageUsers";
import { PositionBadge } from "../../PositionBadge";
import { IEmbeddedFormState } from "../PairingEditorWindow";
import _ from "lodash";
import { v1 } from "uuid";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import { padNumber } from "src/utils/index";

interface INonFlyingAssignmentFormProps {
  isNew: boolean;
  mosaicPane: HTMLElement | null;
  nonFlyingAssignment: INonFlyingAssignment | ICrewsNonFlyingAssignment;
  updateNonFlyingAssignment: (nonFlyingAssignment: any) => void;
  setEmbeddedFormStates?: React.Dispatch<React.SetStateAction<IEmbeddedFormState>>;
  crewOptions?: any[]; // determined whether or not to allow switching crew assigned;
  /** React setState for the embedded initial value of nonFlyingAssignment */
  setSelectedNonFlyingAssignment?: (nonFlyingAssignment: INonFlyingAssignment | null) => void;
}

// New Non Flying Assignment default values.
export const initialValues: INonFlyingAssignment = {
  _id: undefined,
  start: undefined,
  end: undefined,
  locationICAO: undefined,
  locationTimezoneId: undefined,
  locationCountry: undefined,
  assignmentType: undefined,
  createdBy: undefined,
  createdAt: undefined,
  // crew: [], //! Ignored on the backend -when used inside a pairing.
};

const OCNFAForm = styled("div")(({ theme }) => ({
  "& .formik_field_container": {
    "& .formik_field": {
      maxWidth: 350,
    },
    "& .pairing_note_textfield": {
      "& .input_control label": { width: 180 },
    },
    "& p.required_fields_label": {
      margin: 0,
      fontWeight: 300,
      fontSize: 10,
      textTransform: "uppercase",
      color: theme.custom.forms.labels,
    },
    "& .required_fields": {
      backgroundColor: alpha(theme.custom.forms.labels, 0.2),
      margin: "2px 0px",
    },
    "& .form_actions": {
      margin: 10,
      "& button": {
        margin: "0px 2px",
        fontSize: 11,
        lineHeight: "12px",
      },
    },
  },
  "& .event_dropzone": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    flexGrow: 1,
    border: `1px dashed ${theme.custom.forms.labels}`,
    backgroundColor: theme.custom.timeline.contextListBackground,
    color: theme.palette.primary.contrastText,
    borderRadius: 3,
    padding: 20,
    marginBottom: "10px",
    "& .title": { fontSize: 18, fontWeight: "bold" },
    "& .body": { fontSize: 14, fontWeight: 300, textAlign: "center" },
  },
}));

// NonFlightSchema Form Validation with YUP
const NonFlightSchema = Yup.object().shape({
  start: Yup.number().required("Start is required"),
  end: Yup.number().required("End is required").moreThan(Yup.ref("start"), "Assignment must end after its start"),
  locationICAO: Yup.string().required("Location is required"),
  assignmentType: Yup.object().required("Assignment Type is Required"),
});

const PaitingInitialNFASchema = Yup.object().shape({
  assignmentType: Yup.object().required("Assignment Type is Required"),
  crew: Yup.array().required().min(1, "At least one crew member must be assigned."),
});

/** Sort types list Alphabetically */
export const sortTypesByDescription = (a: INonFlyingType, b: INonFlyingType): number => {
  return b.description > a.description ? -1 : 1;
};

export type INonFlyingAssignmentForm = FormikProps<INonFlyingAssignment>;

export const NonFlyingAssignmentForm = (props: INonFlyingAssignmentFormProps): JSX.Element => {
  const {
    isNew,
    mosaicPane,
    nonFlyingAssignment,
    updateNonFlyingAssignment,
    setEmbeddedFormStates,
    crewOptions,
    setSelectedNonFlyingAssignment,
  } = props;
  const { locationICAO } = nonFlyingAssignment ?? {}; //assignmentType
  const dispatch = useDispatch();
  const structures: IStructuresState = useSelector((state: IRootState) => state.structures);
  const { customerStructure, airportOptions } = structures;
  const { nonFlyingTypes } = customerStructure as any; //positions

  // Default mode to local
  const [displayAsUTC, setDisplayAsUTC] = useState<boolean>(false);
  const [loadingTimezones, setLoadingTimezones] = useState<boolean>(false);

  // Load default values for airport data so they display in the selection menus.
  const loadAirportData = useCallback(
    (icao: string) => {
      updateAirportOptions(icao, airportOptions, dispatch);
    },
    [airportOptions, dispatch]
  );

  // Retrieve initial AirportOptions for location if not in airport options.
  useEffect(() => {
    if (locationICAO && !airportOptions[locationICAO]) {
      loadAirportData(locationICAO);
      setLoadingTimezones(true);
    }

    if (!!locationICAO && !!airportOptions[locationICAO]) {
      setLoadingTimezones(false);
    }
  }, [locationICAO, airportOptions, loadAirportData]);

  // If paiting new NFAs in the crew sched, show a limited form.
  const paintNew = crewOptions?.length > 0 && !nonFlyingAssignment?._id;

  const nonFlyingTypesOptions = useMemo(
    () =>
      setEmbeddedFormStates
        ? nonFlyingTypes
            .filter((nonFlyingType: INonFlyingType) => nonFlyingType.enabled && nonFlyingType.allowableOnPairing)
            .sort(sortTypesByDescription)
        : nonFlyingTypes.filter((nonFlyingType: INonFlyingType) => nonFlyingType.enabled).sort(sortTypesByDescription),
    [nonFlyingTypes, setEmbeddedFormStates]
  );

  const handleSubmit = useCallback(
    (
      values: INonFlyingAssignment | ICrewsNonFlyingAssignment,
      helpers: FormikHelpers<INonFlyingAssignment | ICrewsNonFlyingAssignment>
    ): void => {
      if (!values?._id && !values._tempId) {
        // Assign a temp id if no objectid is present (i.e pending creation in draft)
        values._tempId = `temp-${v1()}`;
      }
      if (setSelectedNonFlyingAssignment) setSelectedNonFlyingAssignment(values);
      updateNonFlyingAssignment(values);
      // If used inside a pairing, update pairing and reset form.
      if (setEmbeddedFormStates) {
        // Reset form on submit to initial values (this is required to ensure more events can be added).
        helpers.resetForm({ values: initialValues });
      }
    },
    [setEmbeddedFormStates, updateNonFlyingAssignment]
  );

  return (
    <OCNFAForm>
      {!paintNew ? (
        <Formik
          initialValues={nonFlyingAssignment}
          enableReinitialize={true}
          onSubmit={handleSubmit}
          validationSchema={NonFlightSchema}
          validateOnBlur={true}
        >
          {(formProps: INonFlyingAssignmentForm) => {
            const duration =
              formProps?.values?.end && formProps?.values?.start
                ? Math.abs(formProps?.values?.end?.valueOf() - formProps?.values?.start?.valueOf()) / 1000
                : undefined;
            const hours = duration ? Math.floor(duration / 3600) : undefined; // need to be able to show 24 hours or greater
            const mins = duration ? Math.floor(duration / 60) % 60 : undefined;

            return (
              <>
                <div style={{ display: "flex", margin: "10px 5px" }}>
                  {formProps?.values?._id || formProps?.values?._tempId ? "Edit " : "Add "}
                  Non-Flying Assignment
                </div>

                <Form>
                  <SpinnerProgress loading={loadingTimezones} message="loading timezone data" />
                  <Grid container className="formik_field_container" direction="row" wrap="wrap">
                    <p className="required_fields_label">required fields</p>
                    <Grid container className="required_fields">
                      <FormikSelectField
                        label="Assignment Type"
                        name="assignmentType"
                        options={nonFlyingTypesOptions}
                        getOptionLabel={(type: INonFlyingType) => `${type.description} (${type.title})`}
                        getOptionValue={(type: INonFlyingType) => type._id}
                        isSearchable={true}
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        openMenuOnFocus={false}
                        isClearable={false}
                      />
                      <Grid item>
                        {/* DISPLAY DATE/TIME LOCAL UTC SWITCH */}
                        <SwitchField
                          className={`time_mode_selector ${!displayAsUTC && "active"}`}
                          name={"nfaform_time_mode_selector"}
                          value={displayAsUTC}
                          onChange={() => setDisplayAsUTC(!displayAsUTC)}
                          switchlabels={["UTC", "LOCAL"]}
                          switchtooltips={["Times as UTC", "Times as local"]}
                          label="Display Date/Times in"
                          allowNull={false}
                          disabled={!formProps?.values?.locationICAO}
                        />
                      </Grid>
                      <FormikSelectField
                        label="Location"
                        name="locationICAO"
                        format={(ident: string) => formatIdentFromAirport(ident, Object.values(airportOptions))}
                        parse={(airport: IAirportOption) => {
                          // Set the timezoneId automatically based on the location selection.
                          formProps.setFieldValue("locationTimezoneId", airport.timezoneId);
                          formProps.setFieldTouched("locationTimezoneId", true);
                          return airport.ident;
                        }}
                        onInputChange={(ident: string) => {
                          updateAirportOptions(ident, airportOptions, dispatch);
                        }}
                        options={Object.values(airportOptions)}
                        getOptionLabel={(airport: IAirportOption) => `${airport.ident} - ${airport.name}`}
                        getOptionValue={(airport: IAirportOption) => airport.ident}
                        isSearchable={true}
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        openMenuOnFocus={false}
                        isClearable={false}
                        noOptionsMessage={() => "Search airport name or ICAO..."}
                      />
                      <FormikTextField label="Location Timezone" name="locationTimezoneId" disabled={true} />
                      {crewOptions && crewOptions?.length > 0 && (
                        <FormikSelectField
                          name="crew"
                          label="Crew"
                          menuPortalTarget={mosaicPane}
                          menuPosition={"fixed"}
                          options={crewOptions}
                          parse={(crewValue) => [crewValue]}
                          getOptionLabel={formatUserLabel}
                          formatOptionLabel={(user: IUser) => {
                            const { firstName, lastName, employeeId, crewInfo } = user;
                            const highPos = crewInfo?.positions?.sort((a, b) => a.displayOrder - b.displayOrder)[0];
                            return (
                              <Grid container className="crew_option_container" alignItems="center" wrap="nowrap">
                                {highPos && (
                                  <PositionBadge
                                    position={highPos}
                                    filled={true}
                                    isInactive={!user?.crewInfo?.isActive}
                                  />
                                )}
                                <Grid item style={{ marginLeft: 10 }}>{`${firstName} ${lastName} ${
                                  employeeId ? `[${employeeId}]` : ""
                                }`}</Grid>
                              </Grid>
                            );
                          }}
                          getOptionValue={(user: IUser) => user._id}
                          placeholder="Select Crew"
                          isSearchable={true}
                          isClearable={false}
                        />
                      )}
                      <Grid container style={{ width: "auto" }} alignItems="center">
                        <Grid item>
                          <FormikDateTimeField
                            label="Start Time"
                            name="start"
                            showAirportTime={formProps?.values?.locationICAO}
                            localizeDisplay={!displayAsUTC}
                            hideLocalizeBtn={true}
                            isEpoch={true}
                            maxDate={formProps?.values?.end?.valueOf()}
                          />
                        </Grid>
                        <Grid item>
                          <FormikDateTimeField
                            label="End Time"
                            name="end"
                            showAirportTime={formProps?.values?.locationICAO}
                            localizeDisplay={!displayAsUTC}
                            hideLocalizeBtn={true}
                            isEpoch={true}
                            minDate={formProps?.values?.start?.valueOf()}
                          />
                        </Grid>
                        {typeof hours === "number" && typeof mins === "number" && (
                          <Typography style={{ fontSize: 12, marginLeft: 15 }}>
                            Duration: {`${padNumber(hours, 2)}:${padNumber(mins, 2)}`}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                    <div className="form_actions">
                      <Button type="reset" variant="contained" color="secondary" disabled={!formProps.dirty}>
                        Reset
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!formProps.dirty || !formProps?.isValid}
                      >
                        {isNew ? "Add" : "Modify"}
                      </Button>
                    </div>
                  </Grid>
                </Form>
              </>
            );
          }}
        </Formik>
      ) : (
        <Formik
          initialValues={nonFlyingAssignment}
          enableReinitialize={true}
          onSubmit={handleSubmit}
          validationSchema={PaitingInitialNFASchema}
          validateOnBlur={true}
        >
          {(paintFormProps: INonFlyingAssignmentForm) => {
            return (
              <>
                <div style={{ display: "flex", margin: "10px 5px" }}>Paint New Non-Flying Assignments</div>

                <Form>
                  <Grid container className="formik_field_container" direction="row" wrap="wrap">
                    <p className="required_fields_label">required fields</p>
                    <Grid container className="required_fields">
                      <FormikSelectField
                        label="Assignment Type"
                        name="assignmentType"
                        options={nonFlyingTypesOptions}
                        getOptionLabel={(type: INonFlyingType) => `${type.description} (${type.title})`}
                        getOptionValue={(type: INonFlyingType) => type._id}
                        isSearchable={true}
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        openMenuOnFocus={false}
                        isClearable={false}
                      />
                      <FormikSelectField
                        name="crew"
                        label="Crew"
                        menuPortalTarget={mosaicPane}
                        menuPosition={"fixed"}
                        options={crewOptions}
                        parse={(crewValue) => crewValue ?? []}
                        getOptionLabel={formatUserLabel}
                        formatOptionLabel={(user: IUser) => {
                          const { firstName, lastName, employeeId, crewInfo } = user;
                          const highPos = crewInfo?.positions?.sort((a, b) => a.displayOrder - b.displayOrder)[0];
                          return (
                            <Grid container className="crew_option_container" alignItems="center" wrap="nowrap">
                              {highPos && (
                                <PositionBadge
                                  position={highPos}
                                  filled={true}
                                  isInactive={!user?.crewInfo?.isActive}
                                />
                              )}
                              <Grid item style={{ marginLeft: 10 }}>{`${firstName} ${lastName} ${
                                employeeId ? `[${employeeId}]` : ""
                              }`}</Grid>
                            </Grid>
                          );
                        }}
                        getOptionValue={(user: IUser) => user._id}
                        placeholder="Select Crew"
                        isSearchable={true}
                        isClearable={false}
                      />
                    </Grid>
                    <div className="form_actions">
                      <Button type="reset" variant="contained" color="secondary" disabled={!paintFormProps.dirty}>
                        Reset
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!paintFormProps.dirty || !paintFormProps?.isValid}
                      >
                        Paint
                      </Button>
                    </div>
                  </Grid>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </OCNFAForm>
  );
};
